import {useEffect, useMemo, useRef, useState} from 'react';
import {AnimatePresence, motion} from 'framer-motion';
import {Link} from 'react-router-dom';
import {UserService} from '../../../service/UserService';
import {User} from '../../../model/user/User';
import VideoWithPlayButton from '../video/VideoWithPlayButton';

// ─── Announcement audience ──────────────────────────────────────────────────

type AnnouncementAudience = 'ALL' | 'STUDENTS_ONLY' | 'PARENTS_ONLY' | 'SAAS_ONLY' | 'KED_ONLY';

interface Announcement {
    id: string;
    title: string;
    body: string;
    date: string;
    link?: string;
    linkLabel?: string;
    audience?: AnnouncementAudience;
    videoId?: string;
    videoTitle?: string;
    createdBefore?: string; // ISO date — only show to users created before this date
}

function matchesAudience(announcement: Announcement, user?: User | null): boolean {
    const audience = announcement.audience || 'ALL';
    if (audience === 'ALL' || !user) return true;

    // Filter by user creation date if specified
    if (announcement.createdBefore && user.createdAt) {
        if (new Date(user.createdAt) >= new Date(announcement.createdBefore)) {
            return false;
        }
    }

    switch (audience) {
        case 'STUDENTS_ONLY':
            return !user.isParent;
        case 'PARENTS_ONLY':
            return !!user.isParent;
        case 'SAAS_ONLY':
            return !user.isParent && !!(user.groups && user.groups.length > 0);
        case 'KED_ONLY':
            return user.hasAccessToKED;
        default:
            return true;
    }
}

// Simple config — replace with API call later
const ALL_ANNOUNCEMENTS: Announcement[] = [
    {
        id: 'textbook-support-april-2026',
        title: '🐱 Podręczniki & Pełna podstawa programowa w Planach Nauki',
        body: 'Dodaliśmy wsparcie dla najpopularniejszych podręczników - przy planach nauki automatycznie dopasujemy tematy do rozdziałów z Twojej książki. \nZadania w 100% zgodne z najnowszą podstawą programową.',
        date: '2026-04-15',
        link: '/ustawienia/profil-edukacyjny',
        linkLabel: 'Ustaw podręcznik',
        audience: 'STUDENTS_ONLY',
    },
    {
        id: 'platform-update-march-2026',
        title: 'Co nowego na Produkacji?',
        body: 'Zobacz krótkie podsumowanie najważniejszych zmian i nowości.',
        date: '2026-03-24',
        videoId: '1176633821',
        videoTitle: 'Co nowego na Produkacji?',
        linkLabel: 'Obejrzyj wideo',
        audience: 'STUDENTS_ONLY',
        createdBefore: '2026-03-24T19:00:00+01:00',
    },
];

// ─── localStorage fallback for unauthenticated users ─────────────────────────

const LAST_SEEN_KEY_PREFIX = 'notifications-last-seen';

function getLastSeenKey(): string {
    try {
        const idToken = localStorage.getItem('idToken');
        if (idToken) {
            const payload = JSON.parse(atob(idToken.split('.')[1]));
            if (payload.sub) {
                return `${LAST_SEEN_KEY_PREFIX}-${payload.sub}`;
            }
        }
    } catch { /* fallback to unscoped key */
    }
    return LAST_SEEN_KEY_PREFIX;
}

function getLastSeenTimestamp(): number {
    const raw = localStorage.getItem(getLastSeenKey());
    return raw ? parseInt(raw, 10) : 0;
}

function markAllSeenLocalStorage(): void {
    localStorage.setItem(getLastSeenKey(), Date.now().toString());
}

function countUnreadLocalStorage(announcements: Announcement[]): number {
    const lastSeen = getLastSeenTimestamp();
    return announcements.filter(function (a) {
        return new Date(a.date).getTime() > lastSeen;
    }).length;
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface NotificationBellProps {
    readAnnouncementIds?: string[];
    user?: User | null;
}

// ─── Component ──────────────────────────────────────────────────────────────

export default function NotificationBell(props: NotificationBellProps) {
    const {readAnnouncementIds, user} = props;
    const isAuthenticated = readAnnouncementIds !== undefined;

    // Filter announcements by audience
    const announcements = useMemo(function () {
        return ALL_ANNOUNCEMENTS.filter(function (a) {
            return matchesAudience(a, user);
        });
    }, [user]);

    const allAnnouncementIds = useMemo(function () {
        return announcements.map(function (a) {
            return a.id;
        });
    }, [announcements]);

    // Compute initial unread count
    const initialUnread = isAuthenticated
        ? announcements.filter(function (a) {
            return !readAnnouncementIds.includes(a.id);
        }).length
        : countUnreadLocalStorage(announcements);

    const [isOpen, setIsOpen] = useState(false);
    const [unreadCount, setUnreadCount] = useState(initialUnread);
    const [readIds, setReadIds] = useState<string[]>(isAuthenticated ? readAnnouncementIds : []);
    const [videoModal, setVideoModal] = useState<{ id: string; title: string } | null>(null);
    const bellRef = useRef<HTMLDivElement>(null);

    useEffect(function () {
        function handleClickOutside(e: MouseEvent) {
            if (bellRef.current && !bellRef.current.contains(e.target as Node)) {
                setIsOpen(false);
            }
        }

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }
        return function () {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    function handleToggle() {
        if (!isOpen) {
            if (isAuthenticated) {
                UserService.markAnnouncementsAsRead(allAnnouncementIds);
                setReadIds(allAnnouncementIds);
            } else {
                markAllSeenLocalStorage();
            }
            setUnreadCount(0);
        }
        setIsOpen(!isOpen);
    }

    return (
        <div ref={bellRef} style={{position: 'relative'}}>
            <motion.div
                whileHover={{scale: 1.15}}
                whileTap={{scale: 0.95}}
                style={{cursor: 'pointer', position: 'relative', padding: '4px'}}
                onClick={handleToggle}
            >
                <i
                    className={`bi ${isOpen ? 'bi-bell-fill' : 'bi-bell'}`}
                    style={{
                        fontSize: '1.25rem',
                        color: isOpen ? '#2ad167' : 'rgba(255,255,255,0.7)',
                        transition: 'color 0.2s ease',
                    }}
                />
                {unreadCount > 0 && (
                    <motion.span
                        initial={{scale: 0}}
                        animate={{scale: 1}}
                        transition={{type: 'spring', stiffness: 500, damping: 20}}
                        style={{
                            position: 'absolute',
                            top: '0',
                            right: '-2px',
                            background: '#2ad167',
                            color: '#0f0f1e',
                            borderRadius: '50%',
                            width: '17px',
                            height: '17px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            fontSize: '0.6rem',
                            fontWeight: 700,
                        }}
                    >
                        {unreadCount > 9 ? '9+' : unreadCount}
                    </motion.span>
                )}
            </motion.div>

            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        initial={{opacity: 0, y: -8, scale: 0.96}}
                        animate={{opacity: 1, y: 0, scale: 1}}
                        exit={{opacity: 0, y: -8, scale: 0.96}}
                        transition={{duration: 0.18}}
                        style={{
                            position: 'absolute',
                            top: '44px',
                            right: '-60px',
                            width: '300px',
                            maxHeight: '380px',
                            overflowY: 'auto',
                            background: 'rgba(26, 26, 46, 0.98)',
                            border: '1px solid rgba(42, 209, 103, 0.15)',
                            borderRadius: '12px',
                            boxShadow: '0 12px 32px rgba(0, 0, 0, 0.45)',
                            backdropFilter: 'blur(12px)',
                            zIndex: 2100,
                            padding: '0.5rem 0',
                        }}
                    >
                        <div style={{
                            padding: '0.6rem 1rem 0.6rem',
                            borderBottom: '1px solid rgba(255,255,255,0.07)',
                            fontWeight: 700,
                            fontSize: '0.9rem',
                            color: 'rgba(255,255,255,0.85)',
                            letterSpacing: '-0.01em',
                        }}>
                            Powiadomienia
                        </div>

                        {announcements.length === 0 ? (
                            <div style={{
                                padding: '2rem 1rem',
                                textAlign: 'center',
                                color: 'rgba(255,255,255,0.35)',
                                fontSize: '0.82rem',
                            }}>
                                Brak powiadomień
                            </div>
                        ) : (
                            announcements.map(function (item, index) {
                                const isUnread = isAuthenticated
                                    ? !readIds.includes(item.id)
                                    : new Date(item.date).getTime() > getLastSeenTimestamp();

                                return (
                                    <motion.div
                                        key={item.id}
                                        initial={{opacity: 0, x: -10}}
                                        animate={{
                                            opacity: 1,
                                            x: 0,
                                            transition: {delay: index * 0.04, duration: 0.15},
                                        }}
                                        style={{
                                            padding: '0.7rem 1rem',
                                            borderBottom: index < announcements.length - 1
                                                ? '1px solid rgba(255,255,255,0.04)'
                                                : 'none',
                                        }}
                                    >
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.4rem',
                                            marginBottom: '0.2rem',
                                        }}>
                                            {isUnread && (
                                                <span style={{
                                                    width: '6px',
                                                    height: '6px',
                                                    borderRadius: '50%',
                                                    background: '#2ad167',
                                                    flexShrink: 0,
                                                }}/>
                                            )}
                                            <span style={{
                                                fontWeight: 600,
                                                fontSize: '0.83rem',
                                                color: 'rgba(255,255,255,0.9)',
                                            }}>
                                                {item.title}
                                            </span>
                                        </div>
                                        <div style={{
                                            fontSize: '0.78rem',
                                            color: 'rgba(255,255,255,0.45)',
                                            lineHeight: 1.45,
                                        }}>
                                            {item.body}
                                        </div>
                                        {item.videoId ? (
                                            <button
                                                onClick={function () {
                                                    setIsOpen(false);
                                                    setVideoModal({
                                                        id: item.videoId!,
                                                        title: item.videoTitle || item.title
                                                    });
                                                }}
                                                style={{
                                                    fontSize: '0.73rem',
                                                    color: '#2ad167',
                                                    textDecoration: 'none',
                                                    fontWeight: 600,
                                                    marginTop: '0.3rem',
                                                    display: 'inline-block',
                                                    background: 'none',
                                                    border: 'none',
                                                    padding: 0,
                                                    cursor: 'pointer',
                                                }}
                                            >
                                                <i className="bi bi-play-circle me-1"/>{item.linkLabel || 'Obejrzyj'} →
                                            </button>
                                        ) : item.link ? (
                                            <Link
                                                to={item.link}
                                                onClick={function () {
                                                    setIsOpen(false);
                                                }}
                                                style={{
                                                    fontSize: '0.73rem',
                                                    color: '#2ad167',
                                                    textDecoration: 'none',
                                                    fontWeight: 600,
                                                    marginTop: '0.3rem',
                                                    display: 'inline-block',
                                                }}
                                            >
                                                {item.linkLabel || 'Sprawdź'} →
                                            </Link>
                                        ) : null}
                                    </motion.div>
                                );
                            })
                        )}
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Video modal */}
            <AnimatePresence>
                {videoModal && (
                    <motion.div
                        style={{
                            position: 'fixed',
                            inset: 0,
                            zIndex: 10000,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            background: 'rgba(0, 0, 0, 0.75)',
                            backdropFilter: 'blur(8px)',
                            padding: '1rem',
                        }}
                        onClick={function () {
                            setVideoModal(null);
                        }}
                        initial={{opacity: 0}}
                        animate={{opacity: 1}}
                        exit={{opacity: 0}}
                        transition={{duration: 0.3}}
                    >
                        <motion.div
                            onClick={function (e) {
                                e.stopPropagation();
                            }}
                            initial={{opacity: 0, scale: 0.95, y: 16}}
                            animate={{opacity: 1, scale: 1, y: 0}}
                            exit={{opacity: 0, scale: 0.95, y: 16}}
                            transition={{duration: 0.25, ease: 'easeOut'}}
                            style={{
                                width: '100%',
                                maxWidth: '720px',
                                background: 'linear-gradient(165deg, #1a1f2e 0%, #151a27 100%)',
                                border: '1px solid rgba(255, 255, 255, 0.08)',
                                borderRadius: '16px',
                                overflow: 'hidden',
                            }}
                        >
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                padding: '1.25rem 1.5rem',
                                borderBottom: '1px solid rgba(255, 255, 255, 0.06)',
                            }}>
                                <h5 style={{
                                    margin: 0,
                                    color: '#fff',
                                    fontWeight: 600,
                                    fontSize: '1rem'
                                }}>{videoModal.title}</h5>
                                <button
                                    type="button"
                                    onClick={function () {
                                        setVideoModal(null);
                                    }}
                                    style={{
                                        background: 'rgba(255, 255, 255, 0.06)',
                                        border: '1px solid rgba(255, 255, 255, 0.1)',
                                        borderRadius: '8px',
                                        color: 'rgba(255, 255, 255, 0.5)',
                                        cursor: 'pointer',
                                        padding: '0.3rem 0.6rem',
                                        fontSize: '0.85rem',
                                        lineHeight: 1,
                                    }}
                                >
                                    <i className="bi bi-x-lg"/>
                                </button>
                            </div>
                            <div>
                                <VideoWithPlayButton
                                    id={`notification-video-${videoModal.id}`}
                                    vimeoVideoId={videoModal.id}
                                    hidePlayButton={false}
                                    autoPlay={true}
                                    customDivWrapperClasses="w-100 m-0"
                                />
                            </div>
                            <div style={{
                                padding: '1rem 1.5rem',
                                borderTop: '1px solid rgba(255, 255, 255, 0.06)',
                                display: 'flex',
                                justifyContent: 'flex-end',
                            }}>
                                <button
                                    type="button"
                                    onClick={function () {
                                        setVideoModal(null);
                                    }}
                                    style={{
                                        background: 'linear-gradient(135deg, rgba(42, 209, 103, 0.15), rgba(42, 209, 103, 0.08))',
                                        border: '1px solid rgba(42, 209, 103, 0.2)',
                                        borderRadius: '10px',
                                        color: '#2ad167',
                                        cursor: 'pointer',
                                        padding: '0.5rem 1.25rem',
                                        fontSize: '0.85rem',
                                        fontWeight: 500,
                                    }}
                                >
                                    Zamknij
                                </button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    );
}
